import { IconChevronRightSmall } from "@/assets/icons/geist/IconChevronRightSmall";
import { Counter } from "@/components/flexkit/badge/Counter";
import { cn } from "@/components/podkit/lib/cn";
import { useEffect, type FC, type ReactNode } from "react";
import { Link, useMatches, useNavigate, type UIMatch } from "react-router-dom";
import type { BreadcrumbItem } from "@/contexts/SettingsPageContext";

type BreadcrumbsProps = {
    label?: string | ReactNode;
    customBreadcrumbs?: BreadcrumbItem[] | null;
    /**
     * Size variant for the breadcrumb text.
     * - "default": text-base (for navigation breadcrumbs)
     * - "large": text-2xl (for page headers, original styling)
     */
    size?: "default" | "large";
};

const Breadcrumbs: FC<BreadcrumbsProps> = ({ label, customBreadcrumbs, size = "default" }) => {
    const matches = useMatches() as UIMatch<unknown, { label: string; pathname: string; description?: string }>[];
    const crumbs = matches.filter((match) => Boolean(match.handle));
    const parents = crumbs.slice(0, -1);
    const current = crumbs[crumbs.length - 1];
    const navigate = useNavigate();
    const pathOfParentPage = parents.length > 0 && parents[parents.length - 1].pathname;
    // capture escape key navigate to the previous part of the breadcrumb
    useEffect(() => {
        if (!pathOfParentPage) {
            return;
        }
        const handleKeyDown = (e: KeyboardEvent) => {
            // We only want to handle the event if no other element is focused
            if (document.activeElement?.nodeName !== "BODY") {
                return;
            }

            if (e.key === "Escape") {
                e.stopImmediatePropagation();
                e.stopPropagation();
                e.preventDefault();
                navigate(pathOfParentPage);
            }
        };
        window.addEventListener("keydown", handleKeyDown, { capture: true });
        return () => {
            window.removeEventListener("keydown", handleKeyDown, { capture: true });
        };
    }, [navigate, pathOfParentPage, current]);

    // Build the final breadcrumb items
    let finalBreadcrumbs: BreadcrumbItem[] = [];

    // Filter parents to only include routes with a label
    const parentsWithLabels = parents
        .filter((p) => Boolean(p.handle?.label))
        .map((p) => ({ label: p.handle?.label, to: p.pathname }));

    if (customBreadcrumbs && customBreadcrumbs.length > 0) {
        // Use parent breadcrumbs from routes + custom breadcrumbs
        finalBreadcrumbs = [...parentsWithLabels, ...customBreadcrumbs];
    } else {
        // Use auto-generated breadcrumbs from routes
        finalBreadcrumbs = [...parentsWithLabels, { label: current?.handle?.label ?? label }];
    }

    const isLarge = size === "large";

    return (
        <ol className={cn("flex min-w-0 flex-row items-center", isLarge ? "gap-2 text-2xl" : "gap-0.5 text-base")}>
            {finalBreadcrumbs.map((crumb, index) => {
                const isLast = index === finalBreadcrumbs.length - 1;
                const Icon = crumb.icon;

                if (isLast || !crumb.to) {
                    // Last item or item without link - just display as text
                    return (
                        <li
                            key={index}
                            className={cn(
                                "flex min-w-0 items-center text-content-primary",
                                isLarge
                                    ? "gap-2 text-2xl font-semibold tracking-[-0.2px]"
                                    : "gap-1.5 text-base font-medium",
                            )}
                        >
                            {Icon && <Icon className={cn("shrink-0", isLarge ? "size-6" : "size-4")} />}
                            <span className="truncate">{crumb.label}</span>
                            {crumb.description && <Counter counter={crumb.description} />}
                        </li>
                    );
                }

                // Item with link
                return (
                    <li
                        key={index}
                        className={cn(
                            "flex flex-row items-center text-content-strong",
                            isLarge
                                ? "gap-[2px] text-2xl font-normal tracking-[-0.2px]"
                                : "gap-0.5 text-base font-normal",
                        )}
                    >
                        {Icon && <Icon className={cn("shrink-0", isLarge ? "size-6" : "size-4")} />}
                        <Link to={crumb.to} className="hover:text-content-primary">
                            {crumb.label}
                        </Link>
                        <IconChevronRightSmall size={isLarge ? "lg" : "sm"} className="text-content-tertiary" />
                    </li>
                );
            })}
        </ol>
    );
};

export default Breadcrumbs;
